import { CATEGORY_ICON, CATEGORY_TONE, Icon, type CategoryCode } from '@barev/ui';
import { paramsSummary, useI18n } from '@barev/web';
import type { ReactNode } from 'react';

export const toneClass = (c: string) => `bc-tone-${CATEGORY_TONE[c as CategoryCode] ?? 'white'}`;

export function PageHead({ title, back = true, right }: { title: ReactNode; back?: boolean; right?: ReactNode }) {
  const { t } = useI18n();
  return (
    <div className="page-head">
      {back && (
        <button className="back" onClick={() => window.history.back()} aria-label={t('common.back')}>
          <Icon name="back" />
        </button>
      )}
      <h1 className="bc-h2" style={{ flex: 1 }}>
        {title}
      </h1>
      {right}
    </div>
  );
}

export interface OrderLike {
  id: string;
  number: number;
  serviceCode: string;
  category: string;
  flow: string;
  status: string;
  scheduledAt: Date;
  params: Record<string, unknown>;
}

/** Подпись параметров: «Генеральная · 2 комнаты» */
export function useParamsSummary() {
  const { t } = useI18n();
  return (serviceCode: string, params: Record<string, unknown>) => paramsSummary(t, serviceCode, params);
}

/** Карточка заказа в списке: иконка направления, услуга, время, статус и полоска прогресса */
export function OrderRow({ o }: { o: OrderLike & { active?: boolean; steps?: readonly string[]; rating?: { score: number } | null; done?: boolean } }) {
  const { t, dt } = useI18n();
  const summary = useParamsSummary();
  const idx = o.steps ? o.steps.indexOf(o.status) : -1;
  const progress = o.active && o.steps && idx >= 0 ? (idx + 1) / o.steps.length : null;
  return (
    <a href={`#/orders/${o.id}`} className="order-card" data-status={o.status === 'CANCELLED' ? 'cancelled' : o.active ? 'active' : 'done'}>
      <span className={`order-card__icon ${toneClass(o.category)}`}>
        <Icon name={CATEGORY_ICON[o.category as CategoryCode] ?? 'home'} size={22} />
      </span>
      <span className="order-card__main">
        <span className="order-card__title">{t(`services.${o.serviceCode}.name`)}</span>
        <span className="order-card__sub">{summary(o.serviceCode, o.params)}</span>
        <span className="order-card__when">{dt(o.scheduledAt, { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
        {progress !== null && (
          <span className="order-card__bar" aria-hidden="true">
            <span style={{ width: `${Math.round(progress * 100)}%` }} />
          </span>
        )}
      </span>
      <span className="order-card__side">
        <span className="order-card__status">{t(`status.${o.flow}.${o.status}`)}</span>
        {o.rating ? <Stars value={o.rating.score} size={12} /> : o.done ? <span className="order-card__rate">{t('client.rate.prompt')}</span> : null}
      </span>
    </a>
  );
}

export type Level = 'BAREV' | 'BAREV_PLUS' | 'ELITE';
export const LEVEL_NAME: Record<Level, string> = { BAREV: 'Barev', BAREV_PLUS: 'Barev+', ELITE: 'Barev Elite' };
/** Цвет плашки уровня: как карты на сайте — Barev+ розовая, Elite жёлтая */
export const LEVEL_TONE: Record<Level, { bg: string; ink: string }> = {
  BAREV: { bg: 'var(--bc-surface)', ink: 'var(--bc-ink)' },
  BAREV_PLUS: { bg: 'var(--bc-pink)', ink: 'var(--bc-plum)' },
  ELITE: { bg: 'var(--bc-perks)', ink: 'var(--bc-plum)' },
};

/** Скидка клиента на направление по его уровню; у Elite без своей скидки — скидка Barev+ */
export function levelDiscount(level: Level, c: { barevPlusDiscountPct: number; eliteDiscountPct: number }) {
  if (level === 'ELITE') return Math.max(c.eliteDiscountPct, c.barevPlusDiscountPct);
  return level === 'BAREV_PLUS' ? c.barevPlusDiscountPct : 0;
}

export function LevelBadge({ level }: { level: Level }) {
  const tone = LEVEL_TONE[level];
  return (
    <a href="#/membership" style={{ textDecoration: 'none' }}>
      <span className="bc-badge" style={{ background: tone.bg, color: tone.ink, padding: '8px 14px', fontSize: 13 }}>
        {LEVEL_NAME[level]}
      </span>
    </a>
  );
}

export type RelocationNames = Record<string, { name: Record<string, string>; hint: Record<string, string> | null }>;
/** Название шага или документа: своё у клиента (title) или из общего списка на языке клиента */
export function itemName(names: RelocationNames | undefined, kind: 'STEP' | 'DOC', item: { code: string; title: string | null }, locale: string) {
  if (item.title) return item.title;
  const n = names?.[`${kind}:${item.code}`]?.name;
  return n?.[locale] || n?.ru || item.code;
}
export function itemHint(names: RelocationNames | undefined, kind: 'STEP' | 'DOC', item: { code: string; title: string | null }, locale: string) {
  if (item.title) return null;
  const h = names?.[`${kind}:${item.code}`]?.hint;
  return h?.[locale] || h?.ru || null;
}

export interface TimelineItem {
  label: ReactNode;
  state: 'done' | 'current' | 'todo';
  meta?: ReactNode;
}

/** Вертикальная шкала как на сайте: зелёные точки — пройдено, розовая — сейчас */
export function Timeline({ items, compact }: { items: TimelineItem[]; compact?: boolean }) {
  return (
    <ol className={`tl${compact ? ' tl--compact' : ''}`}>
      {items.map((s, i) => (
        <li key={i} className={`tl__item tl__item--${s.state}`}>
          <span className="tl__dot" aria-hidden="true">
            {s.state === 'done' && <Icon name="check" size={12} strokeWidth={3} />}
          </span>
          <span className="tl__body">
            <span className="tl__label">{s.label}</span>
            {s.meta && <span className="tl__meta">{s.meta}</span>}
          </span>
        </li>
      ))}
    </ol>
  );
}

export interface OrderSteps {
  flow: string;
  status: string;
  /** false — заказ завершён: последний шаг тоже зелёный */
  active?: boolean;
  steps: readonly string[];
  history: { to: string; event: string | null; at: Date }[];
}

/** Шаги заказа по истории статусов: пройденные, текущий, остальные */
export function useOrderTimeline() {
  const { t, dt } = useI18n();
  return (o: OrderSteps): TimelineItem[] => {
    const passed = new Set(o.history.map((h) => h.to));
    if (o.status === 'CANCELLED')
      return [...o.steps.filter((s) => passed.has(s)).map((s) => ({ label: t(`status.${o.flow}.${s}`), state: 'done' as const })), { label: t(`status.${o.flow}.CANCELLED`), state: 'current' as const }];
    const currentIdx = o.steps.indexOf(o.status);
    return o.steps.map((s, i) => {
      const at = o.history.find((h) => h.to === s && !h.event)?.at ?? o.history.find((h) => h.to === s)?.at;
      return {
        label: t(`status.${o.flow}.${s}`),
        state: i < currentIdx || (i === currentIdx && o.active === false) ? 'done' : i === currentIdx ? 'current' : 'todo',
        meta: at && i <= currentIdx ? dt(at, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : undefined,
      };
    });
  };
}

const STAR = 'M12 3l2.7 5.6 6.1.9-4.4 4.3 1 6.1L12 17l-5.4 2.9 1-6.1L3.2 9.5l6.1-.9z';

/** Звёзды 1–5; без onChange — только показ */
export function Stars({ value, onChange, size = 34 }: { value: number; onChange?: (v: number) => void; size?: number }) {
  const { t } = useI18n();
  return (
    <div className="stars" role={onChange ? 'radiogroup' : 'img'} aria-label={onChange ? undefined : t('client.rate.star', { count: value })}>
      {[1, 2, 3, 4, 5].map((n) => {
        const svg = (
          <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
            <path d={STAR} fill={n <= value ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={1.8} strokeLinejoin="round" />
          </svg>
        );
        return onChange ? (
          <button key={n} type="button" role="radio" aria-checked={n === value} aria-label={t('client.rate.star', { count: n })} data-on={n <= value || undefined} onClick={() => onChange(n)}>
            {svg}
          </button>
        ) : (
          <span key={n} data-on={n <= value || undefined}>
            {svg}
          </span>
        );
      })}
    </div>
  );
}

/** Клубная карта-«билет» в стиле карт на сайте */
export function ClubTicket({ level, name, code, until, href, action }: { level: Level; name?: string; code?: string | null; until?: ReactNode; href?: string; action?: ReactNode }) {
  const id = level === 'BAREV' ? 'free' : level === 'BAREV_PLUS' ? 'plus' : 'elite';
  const body = (
    <>
      <span className="ticket__main">
        <span className="ticket__club">Barev Club</span>
        <span className="ticket__level">{LEVEL_NAME[level]}</span>
        <span className="ticket__meta">
          {name && <b>{name}</b>}
          {code && <span className="bc-num">{code}</span>}
        </span>
        {until && <span className="ticket__until">{until}</span>}
      </span>
      {action && <span className="ticket__stub">{action}</span>}
    </>
  );
  return href ? (
    <a className="ticket" data-plan={id} href={href}>
      {body}
    </a>
  ) : (
    <div className="ticket" data-plan={id}>
      {body}
    </div>
  );
}

export function QrGlyph({ size = 28 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinejoin="round" aria-hidden="true">
      <path d="M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h2v2h-2zM18 14h2M14 18v2M18 18h2v2h-2z" />
    </svg>
  );
}
